import React, { useState, useEffect, useCallback } from "react";
import {
  FaTerminal,
  FaCalendarDay,
  FaUser,
  FaTimes,
  FaHistory,
  FaArrowUp,
  FaArrowDown,
  FaCalculator,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function PosShiftSummary({ isOpen, onClose, userId = null }) {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Native browser formatter config
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "0.00 ETB";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const fetchDailyReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = "pos/daily-summary";
      const response = await apiRequest(url);
      if (response?.success) {
        setReportData(response?.data);
      } else {
        setError(response?.message || "Failed to parse report figures.");
      }
    } catch (err) {
      console.error("POS Shift summary payload error:", err);
      setError("Network or server failure retrieving shift summary matrix.");
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      fetchDailyReport();
    }
  }, [isOpen, fetchDailyReport]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-fadeIn">
      {/* Glassmorphism Wrapper Container */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white/[0.03] border border-white/[0.08] backdrop-blur-2xl rounded-3xl p-6 md:p-8 shadow-2xl text-slate-100 flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex items-start justify-between border-b border-white/5 pb-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-black tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase">
              Daily POS Report
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mt-1">
              <span className="flex items-center gap-1.5">
                <FaUser className="text-blue-400" />{" "}
                {reportData?.cashierName || "..."}
              </span>
              <span className="flex items-center gap-1.5">
                <FaCalendarDay className="text-emerald-400" />{" "}
                {reportData?.date || "..."}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition duration-200"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {isLoading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-slate-500 tracking-widest uppercase">
              Compiling Daily Reports...
            </p>
          </div>
        ) : error ? (
          <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-center text-sm text-rose-400">
            {error}
          </div>
        ) : (
          <>
            {/* 2. THE MATHEMATICAL GRID REPORT */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left & Middle Panels: Mathematical Itemizations */}
              <div className="lg:col-span-2 flex flex-col gap-3 bg-slate-900/20 border border-white/5 p-5 rounded-2xl">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-2">
                  <FaCalculator className="text-blue-400" /> Daily Cash Records
                </span>

                <div className="flex items-center justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-slate-400">Opening Cash</span>
                  <span className="font-mono text-slate-200">
                    {formatCurrency(reportData?.openingCash)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 text-emerald-400 font-medium">
                  <span className="flex items-center gap-2">+ Cash Sales</span>
                  <span className="font-mono">
                    {formatCurrency(reportData?.cashSales)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 text-blue-400 font-medium">
                  <span className="flex items-center gap-2">
                    + Cash In (Float Additions)
                  </span>
                  <span className="font-mono">
                    {formatCurrency(reportData?.cashIn)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm py-1.5 text-rose-400 font-medium">
                  <span className="flex items-center gap-2">
                    - Cash Out (Payouts / Expenses)
                  </span>
                  <span className="font-mono">
                    ({formatCurrency(reportData?.cashOut)})
                  </span>
                </div>
              </div>

              {/* Right Panel: Reconciled Balances & Variances */}
              <div className="flex flex-col justify-between gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border border-blue-500/10 backdrop-blur-md">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">
                    Expected Cash in Drawer
                  </span>
                  <h4 className="text-xl font-black text-blue-400 mt-1 font-mono">
                    {formatCurrency(reportData?.expectedCash)}
                  </h4>
                </div>

                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/5 to-indigo-500/5 border border-purple-500/10 backdrop-blur-md">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">
                    Actual Counted Cash
                  </span>
                  <h4 className="text-xl font-black text-purple-400 mt-1 font-mono">
                    {formatCurrency(reportData?.countedCash)}
                  </h4>
                </div>

                <div
                  className={`p-4 rounded-xl border backdrop-blur-md ${
                    (reportData?.variance || 0) >= 0
                      ? "bg-emerald-500/5 border-emerald-500/10 text-emerald-400"
                      : "bg-rose-500/5 border-rose-500/10 text-rose-400"
                  }`}
                >
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">
                    Discrepancy / Variance
                  </span>
                  <h4 className="text-xl font-black mt-1 font-mono">
                    {(reportData?.variance || 0) > 0 ? "+" : ""}
                    {formatCurrency(reportData?.variance)}
                  </h4>
                </div>
              </div>
            </div>

            {/* 3. SHIFT TIMELINE RECORD LIST (UNPAGINATED FLAT LIST) */}
            <div className="flex flex-col gap-3">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <FaHistory className="text-slate-500" />
                Log Details
              </h4>

              <div className="overflow-x-auto w-full border border-white/5 rounded-2xl bg-slate-950/20">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-white/[0.02] border-b border-white/5 text-slate-400 font-semibold">
                      <th className="p-3.5">Terminal</th>
                      <th className="p-3.5">Time</th>
                      <th className="p-3.5 text-right">Opening Balance</th>
                      <th className="p-3.5 text-right">Cash Collected</th>
                      <th className="p-3.5 text-center"> Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-slate-300 font-medium">
                    {reportData?.shiftRecords &&
                    reportData.shiftRecords.length > 0 ? (
                      reportData.shiftRecords.map((shift) => (
                        <tr
                          key={shift.id}
                          className="hover:bg-white/[0.01] transition duration-150"
                        >
                          <td className="p-3.5 font-mono text-slate-400 flex items-center gap-1.5">
                            <FaTerminal className="text-[10px] text-blue-500/60" />{" "}
                            {shift.terminalId}
                          </td>
                          <td className="p-3.5 whitespace-nowrap text-[11px]">
                            <div>
                              Open:{" "}
                              <span className="text-slate-400 font-mono">
                                {new Date(shift.openedAt).toLocaleTimeString(
                                  [],
                                  { hour: "2-digit", minute: "2-digit" }
                                )}
                              </span>
                            </div>
                            {shift.closedAt && (
                              <div className="text-slate-500">
                                Close:{" "}
                                <span className="font-mono">
                                  {new Date(shift.closedAt).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="p-3.5 text-right font-mono">
                            {formatCurrency(shift.openingCash)}
                          </td>
                          <td className="p-3.5 text-right font-mono text-emerald-400">
                            {formatCurrency(shift.cashSales)}
                          </td>
                          <td className="p-3.5 text-center whitespace-nowrap">
                            <span
                              className={`px-2.5 py-0.5 text-[9px] uppercase font-black tracking-widest rounded-full ${
                                shift.status === "OPEN"
                                  ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 animate-pulse"
                                  : "bg-slate-500/10 text-slate-400 border border-white/10"
                              }`}
                            >
                              {shift.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="p-8 text-center text-slate-500 tracking-wide uppercase text-[11px]"
                        >
                          No active records found for this timeline.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
